import { useState } from "react";
import { X, Phone } from "lucide-react";
import { Button } from "../ui/button";

export const CallbackModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    preferredTime: "",
    message: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Build email body
    const subject = encodeURIComponent(`Callback Request from ${formData.name}`);
    const body = encodeURIComponent(
      `Name: ${formData.name}\n` +
      `Email: ${formData.email}\n` +
      `Phone: ${formData.phone}\n` +
      `Preferred Callback Time: ${formData.preferredTime || "Not specified"}\n` +
      `Message: ${formData.message || "No message provided"}`
    );
    
    // Open mailto link
    window.location.href = `mailto:support@iq-hub.com?subject=${subject}&body=${body}`;
    
    // Reset form and close modal
    setFormData({
      name: "",
      email: "",
      phone: "",
      preferredTime: "",
      message: "",
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-[#0F172A] px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#C8A951]/20 flex items-center justify-center">
              <Phone className="w-5 h-5 text-[#C8A951]" />
            </div>
            <div>
              <h3 className="text-white font-bold text-lg">Request a Call</h3>
              <p className="text-[#94A3B8] text-sm">We'll get back to you shortly</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="text-[#94A3B8] hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Name - Required */}
          <div>
            <label className="block text-sm font-medium text-[#0F172A] mb-1.5">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Your full name"
              className="w-full px-4 py-3 rounded-lg border border-[#E2E8F0] focus:border-[#C8A951] focus:ring-2 focus:ring-[#C8A951]/20 outline-none transition-all text-[#0F172A]"
            />
          </div>
          
          {/* Email - Required */}
          <div>
            <label className="block text-sm font-medium text-[#0F172A] mb-1.5">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="your@email.com"
              className="w-full px-4 py-3 rounded-lg border border-[#E2E8F0] focus:border-[#C8A951] focus:ring-2 focus:ring-[#C8A951]/20 outline-none transition-all text-[#0F172A]"
            />
          </div>
          
          {/* Phone - Required */}
          <div>
            <label className="block text-sm font-medium text-[#0F172A] mb-1.5">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              placeholder="+1 (555) 000-0000"
              className="w-full px-4 py-3 rounded-lg border border-[#E2E8F0] focus:border-[#C8A951] focus:ring-2 focus:ring-[#C8A951]/20 outline-none transition-all text-[#0F172A]"
            />
          </div>
          
          {/* Preferred Time - Optional */}
          <div>
            <label className="block text-sm font-medium text-[#0F172A] mb-1.5">
              Preferred Callback Time
            </label>
            <select
              name="preferredTime"
              value={formData.preferredTime}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-lg border border-[#E2E8F0] focus:border-[#C8A951] focus:ring-2 focus:ring-[#C8A951]/20 outline-none transition-all text-[#0F172A] bg-white"
            >
              <option value="">Select a time (optional)</option>
              <option value="Morning (9am - 12pm)">Morning (9am - 12pm)</option>
              <option value="Afternoon (12pm - 5pm)">Afternoon (12pm - 5pm)</option>
              <option value="Evening (5pm - 8pm)">Evening (5pm - 8pm)</option>
              <option value="Anytime">Anytime</option>
            </select>
          </div>
          
          {/* Message - Optional */}
          <div>
            <label className="block text-sm font-medium text-[#0F172A] mb-1.5">
              Message
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Tell us how we can help... (optional)"
              rows={3}
              className="w-full px-4 py-3 rounded-lg border border-[#E2E8F0] focus:border-[#C8A951] focus:ring-2 focus:ring-[#C8A951]/20 outline-none transition-all text-[#0F172A] resize-none"
            />
          </div>
          
          {/* Submit Button */}
          <Button 
            type="submit"
            className="w-full bg-[#C8A951] hover:bg-[#B8993F] text-[#0F172A] font-semibold py-6 rounded-lg"
          >
            <Phone className="w-5 h-5 mr-2" />
            Request Callback
          </Button>
          
          <p className="text-xs text-[#64748B] text-center">
            We typically respond within 24 hours
          </p>
        </form>
      </div>
    </div>
  );
};
